import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";

import { ordenar } from "../regras";

// A 1ª coluna (o nome) fica parada quando a tabela rola de lado no celular
const FIXA = "sticky left-0 z-[1] bg-card";

/**
 * Tabela que ordena ao tocar no título da coluna. Coluna com `barra` ganha uma
 * barrinha atrás do número (proporção ao maior da coluna). Mostra `limite`
 * linhas e um "Ver todas" no fim. No celular rola de lado dentro do cartão.
 *
 * colunas: [{ key, rotulo, formatar?, render?, barra?, dica?, esquerda? }]
 */
export default function Tabela({ linhas, colunas, ordemInicial, limite = 10, e2e, vazio = "Nada no período." }) {
  const [ordem, setOrdem] = useState(ordemInicial || { key: colunas[1]?.key, dir: "desc" });
  const [todas, setTodas] = useState(false);
  const ordenadas = useMemo(() => ordenar(linhas || [], ordem.key, ordem.dir), [linhas, ordem]);
  const maximos = useMemo(() => Object.fromEntries(colunas.filter((c) => c.barra)
    .map((c) => [c.key, Math.max(0, ...(linhas || []).map((l) => Number(l[c.key]) || 0))])), [colunas, linhas]);

  if (!linhas?.length) return <p className="text-sm text-muted-foreground py-6 text-center">{vazio}</p>;
  const visiveis = todas ? ordenadas : ordenadas.slice(0, limite);
  const trocar = (key) => setOrdem((o) => (o.key === key ? { key, dir: o.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  return (
    <div data-e2e={e2e}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b">
              {colunas.map((c, i) => {
                const ativa = ordem.key === c.key;
                const Seta = ordem.dir === "asc" ? ArrowUp : ArrowDown;
                return (
                  <th key={c.key} className={`py-2 px-2 font-semibold text-[11px] uppercase tracking-wide text-muted-foreground whitespace-nowrap ${i === 0 || c.esquerda ? "text-left" : "text-right"} ${i === 0 ? FIXA : ""}`}>
                    <button type="button" onClick={() => trocar(c.key)} title={c.dica}
                      className={`inline-flex items-center gap-1 hover:text-foreground ${ativa ? "text-foreground" : ""}`}>
                      {c.rotulo}
                      {ativa && <Seta className="w-3 h-3" />}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((l, n) => (
              <tr key={l.id || l.nome || l.numero || n} className="border-b last:border-0 hover:bg-muted/40">
                {colunas.map((c, i) => {
                  const v = l[c.key];
                  const conteudo = c.render ? c.render(l, n) : c.formatar ? c.formatar(v, l) : v;
                  const pct = c.barra && maximos[c.key] ? ((Number(v) || 0) / maximos[c.key]) * 100 : 0;
                  return (
                    <td key={c.key} className={`py-2 px-2 tabular-nums whitespace-nowrap ${i === 0 || c.esquerda ? "text-left" : "text-right"} ${i === 0 ? FIXA : ""}`}>
                      {c.barra ? (
                        <div className="relative flex justify-end items-center min-w-[96px]">
                          <span className="absolute inset-y-0.5 right-0 rounded bg-primary/15" style={{ width: `${pct}%` }} />
                          <span className="relative px-1 font-semibold">{conteudo}</span>
                        </div>
                      ) : conteudo}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ordenadas.length > limite && (
        <button type="button" onClick={() => setTodas((t) => !t)}
          className="mt-2 w-full inline-flex items-center justify-center gap-1 text-xs font-medium text-primary hover:underline py-1.5">
          {todas ? "Mostrar menos" : `Ver todas (${ordenadas.length})`}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${todas ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}
